import { css, jsx } from '@emotion/core';
import { Icon, Skeleton } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { generateHTMLString } from '@/src/pdf/templates/newRelease';
import { convertHtmlToPdf } from '@/src/pdf/utils';
import AdminLink from '@/src/shared/components/AdminLink';
import AttachmentLink from '@/src/shared/components/AttachmentLink';
import Button from '@/src/shared/components/Button';
import ContactCard from '@/src/shared/components/ContactCard';
import ContactCTA from '@/src/shared/components/ContactCTA';
import ContactLinks from '@/src/shared/components/ContactLinks';
import Container from '@/src/shared/components/Container';
import Head from '@/src/shared/components/Head';
import Heading from '@/src/shared/components/Heading';
import Hero from '@/src/shared/components/Hero';
import Layout from '@/src/shared/components/Layout';
import Link from '@/src/shared/components/Link';
import linkStyle from '@/src/shared/components/Link/style';
import Sanitize from '@/src/shared/components/Sanitize';
import Section from '@/src/shared/components/Section';
import Table from '@/src/shared/components/Table';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { bp, space } from '@/styles';
import { convertToFullMonth } from '@/utils/dateTimeHelpers';

import contentStyle from '../../contentStyle';
import HeroList from './Publication/PublicationHero/HeroList';

const style = {
  body: [
    contentStyle,
    css`
      padding: 40px 16px;
      background-color: white;
      box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);
      max-width: 970px;
      border-radius: 4px;
      margin: 0 -16px;
      @media ${bp.md} {
        padding: 52px 32px;
        margin: 0 auto;
      }
      @media print {
        box-shadow: none;
        padding: 16px 0;
        margin: 0 auto;
      }
    `,
  ],
  content: css`
    max-width: 768px;
    .sanitized {
      ul,
      ol {
        margin-left: 32px;
      }
      .lineBreak {
        display: block;
        margin-bottom: 16px;
      }
      a {
        ${linkStyle.link};
      }
      p,
      span,
      em,
      b,
      li {
        &:empty {
          display: none;
        }
      }
    }
  `,
  links: css`
    width: 100%;
    @media ${bp.md} {
      width: calc(50% - 8px);
      max-width: 600px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
    }
  `,
  heroinner: {
    container: css`
      width: 100%;
      display: block;
      margin-bottom: -16px !important;
      @media ${bp.md} {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
      }
    `,
    lhs: css`
      flex: 1;
      margin-bottom: 16px;
      @media ${bp.sm} {
        margin-right: 32px;
      }
      @media print {
        margin-right: 0;
      }
    `,
    rhs: css`
      flex: 0 0 auto;
      margin-bottom: 16px;
      .ant-btn-group {
        @media print {
          display: none;
        }
      }
    `,
  },
  boilerplate: css`
    display: flex;
    margin-top: 75px;
    flex-direction: column;
    align-items: flex-start;
    @media ${bp.md} {
      flex-direction: row;
    }
  `,
  footer: css`
    display: flex;
    margin-top: 30px;
  `,
  center: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  attch: css`
    ${linkStyle.link};
    display: inline-block;
  `,
  attchIcon: css`
    font-size: 1.8rem;
    margin-right: 8px;
  `,
  noPrint: css`
    @media print {
      display: none;
    }
  `,
};

const Subsection = props => {
  const { headingText, children, id, css: _css = css``, ..._props } = props;
  return (
    <div id={id}>
      <Heading type={3} weight="bolder" css={[space.m.b16, _css]} theme="primaryGray" {..._props}>
        {headingText}
      </Heading>
      <div css={space.m.b32}>{children}</div>
    </div>
  );
};

const CMSPage = ({ data, type, path, loading }) => {
  const [fileAttachments, setFileAttachments] = useState([]);
  const [pdfExportStatus, setPdfExportStatus] = useState(false);
  const {
    Cat1Id,
    Title,
    ExecutiveSummary,
    SitePageId,
    Body,
    Attachments,
    WebPosterId,
    OtherEditors,
    ContentGroups = [],
    ContentGroupItems = [],
  } = data;

  const ContentGroupPriorityItemsExist =
    ContentGroups.some(c => c.Text === 'Priority Issues') &&
    ContentGroupItems.some(c => c.ContentGroupId === '1');
  const PriorityIssues = ContentGroupItems.filter(item => item.ContentGroupId === '1');

  const attachmentsExist = Array.isArray(Attachments) && Attachments.length > 0;

  const passed2YearMark = data.PublishDateTime && moment().diff(data.PublishDateTime, 'years') >= 2;
  const isNewsRelease = Cat1Id === 9998;
  const isStandard = data.StandardNr === 'Y';
  const aboveTitleText = isStandard ? 'FOR RELEASE ON RECEIPT' : 'SETTING THE RECORD STRAIGHT';

  useEffect(() => {
    if (!SitePageId) return;
    if (attachmentsExist) {
      const handleAttachments = async () => {
        const filesList = [];
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < data.Attachments.length; i++) {
          // eslint-disable-next-line no-await-in-loop
          // const response = await request({
          //   url: 'attachment/file',
          //   method: 'GET',
          //   responseType: 'blob',
          //   params: {
          //     area: 'c',
          //     id: data.Attachments[i].AttachmentId,
          //   },
          //   triggerLoad: false,
          // });
          // const contentType = response.headers['content-type'];
          // const blob = new Blob([response.data], { type: contentType, encoding: 'UTF-8' });
          // const url = window.URL.createObjectURL(blob);
          const url = `/attachment/page/${data.Attachments[i].AttachmentId}`;
          const newObj = {
            ...data.Attachments[i],
            href: url,
            // blob: detectIE() && blob,
          };
          filesList.push(newObj);
        }
        setFileAttachments(filesList);
      };
      handleAttachments();
    }
  }, [SitePageId]);

  const hide = {
    StaffContact: !data.StaffContact,
    AlternateContact: !data.AlternateContact,
    AlternateContact2: !data.AlternateContact2,
  };

  const contacts = [];
  if (!passed2YearMark) {
    if (!hide.StaffContact) {
      contacts.push({
        fullname: data.StaffContact,
        phone: data.StaffContactPhone,
        email: data.StaffContactEmail,
        title: data.StaffContactTitle,
        id: data.StaffContactId,
        displayPicture: data.StaffContactDisplayPicture === 'Y',
        initials: data.StaffContactInitials,
        companyid: data.StaffContactCompanyId,
        company: data.StaffContactCompany,
      });
    }
    if (!hide.AlternateContact) {
      contacts.push({
        fullname: data.AlternateContact,
        phone: data.AlternateContactPhone,
        email: data.AlternateContactEmail,
        title: data.AlternateContactTitle,
        id: data.AlternateContactId,
        displayPicture: data.AlternateContactDisplayPicture === 'Y',
        initials: data.AlternateContactInitials,
        companyid: data.AlternateContactCompanyId,
        company: data.AlternateContactCompany,
      });
    }
    if (!hide.AlternateContact2) {
      contacts.push({
        fullname: data.AlternateContact2,
        phone: data.AlternateContact2Phone,
        email: data.AlternateContact2Email,
        title: data.AlternateContact2Title,
        id: data.AlternateContact2Id,
        displayPicture: data.AlternateContact2DisplayPicture === 'Y',
        initials: data.AlternateContact2Initials,
        companyid: data.AlternateContact2CompanyId,
        company: data.AlternateContact2Company,
      });
    }
  }

  const isLegacy = new Date(data.PublishDateTime) < new Date('03/01/2020 12:00:00 AM');

  const handleExportToPdf = async () => {
    setPdfExportStatus(true);
    const fileName = `New Release - ${Title}.pdf`;
    const html = generateHTMLString({ data, type, contacts });
    await convertHtmlToPdf({ html, fileName }, setPdfExportStatus);
  };

  return (
    <Layout>
      {loading ? null : <Head title={Title} description={ExecutiveSummary} />}
      <AdminLink
        pageID={SitePageId}
        authorId={WebPosterId}
        editorIds={OtherEditors?.length ? OtherEditors.map(e => e.PersonId) : []}
        pageType="release"
      />
      <Hero
        title={loading ? <Skeleton title={{ width: '50%' }} paragraph={false} /> : Title}
        overrideLast={{
          path,
          name: loading ? '' : Title,
        }}
        aboveTitle={
          data.PublishDateTime &&
          `<h5>${aboveTitleText}</h5><div class='lineBreak'></div>
          ${convertToFullMonth(data.PublishDateTime)}
        `
        }
      >
        <div css={style.heroinner.container}>
          <section css={style.heroinner.lhs}>
            {passed2YearMark ? (
              <ContactCTA />
            ) : (
              <ContactLinks
                title={`Contact${contacts.length > 1 ? 's' : ''}:`}
                contacts={contacts}
              />
            )}
          </section>
          <section css={style.heroinner.rhs}>
            <Button.Group size="small" theme="gray" css={style.noPrint}>
              {attachmentsExist && (
                <Button href="#relatedinformation" htmlType="button">
                  <Icon type="info-circle" /> Related Information
                </Button>
              )}
              <Button onClickPrint>
                <Icon type="printer" theme="filled" /> Print
              </Button>
              <Button onClickExportPDF={handleExportToPdf} disabled={pdfExportStatus || loading}>
                <Icon type="file-pdf" theme="filled" /> Export PDF
              </Button>
            </Button.Group>
          </section>
        </div>
        {ContentGroupPriorityItemsExist && type === 'newsrelease' && (
          <div css={style.heroinner.container}>
            <section css={style.heroinner.lhs}>
              <HeroList
                heading="Priority Issues"
                list={PriorityIssues}
                codeName="ContentItemId"
                descName="ContentItem"
                hrefInfo="ContentItemId"
                link
              />
            </section>
          </div>
        )}
      </Hero>

      <Section bgColor="gray">
        <Container>
          <main css={[style.content, style.body]}>
            {loading ? (
              <Skeleton paragraph={{ rows: 12 }} active />
            ) : (
              <>
                <Sanitize>
                  {(Body || '').replace(/<br \/><br \/>/g, "<div class='lineBreak'></div>")}
                </Sanitize>
                <section css={style.boilerplate}>{data.NewsReleaseFooter}</section>
                <div css={style.center}>###</div>
                {type !== 'publication' && attachmentsExist ? (
                  <Subsection
                    headingText={isLegacy ? 'Related Information' : 'Attachments'}
                    id="relatedinformation"
                    {...(isLegacy
                      ? {
                          css: space.m.t32,
                        }
                      : null)}
                  >
                    <Table
                      css={style.attachmentContainer}
                      showHeader={false}
                      dataSource={[...fileAttachments]}
                      rowKey="AttachmentId"
                      pagination={false}
                      columns={[
                        {
                          dataIndex: 'AttachmentId',
                          render: (id, attch) => {
                            // const ext = attch.FileName.substr(attch.FileName.lastIndexOf('.') + 1);
                            // const isICS = ext && ext.toLowerCase() === 'ics';
                            return (
                              <AttachmentLink
                                attch={attch}
                                linkStyle={style.attch}
                                iconStyle={style.attchIcon}
                                // download={isICS}
                              />
                            );
                          },
                        },
                      ]}
                    />
                  </Subsection>
                ) : null}
                <div css={style.footer}>
                  <Link href="/legal/copyright">Copyright Notice</Link>
                  <span css={style.legalSeparator}>&nbsp;|&nbsp;</span>
                  <Link href="/legal/disclaimer">Legal Disclaimer</Link>
                </div>
              </>
            )}
          </main>
        </Container>
      </Section>

      {contacts && contacts.length ? (
        <Section>
          <Container>
            <ContactCard
              title={`Contact${contacts.length > 1 ? 's' : ''}`}
              contacts={contacts}
              isNewsRelease
            />
          </Container>
        </Section>
      ) : null}
      <WebLogImage browserPath title={Title} sitePageId={SitePageId} />
    </Layout>
  );
};

CMSPage.propTypes = {
  data: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['meeting', 'publication', 'newsrelease']).isRequired,
  path: PropTypes.string.isRequired,
};

export default CMSPage;
